import type { PhoneInput } from 'application/ui/fields/phone/types'
import {
  isPastDate,
  isFutureOrTodayDate,
  parseMaskedDate
} from 'application/ui/fields/date'

/**
 * Валидирует форму.
 * Проверяет все поля формы на соответствие требованиям.
 *
 * @private
 * @param {HTMLFormElement} form - Форма для валидации
 * @return {boolean} true, если форма валидна, иначе false
 */
export function validateForm(form: HTMLFormElement): boolean {
  let isValid = true

  const fields = form.querySelectorAll<HTMLElement>('[data-field]')
  clearErrors(form)

  fields.forEach((field) => {
    const validationType = field.dataset.validationType

    switch (validationType) {
      case 'password': {
        const input = field.querySelector<HTMLInputElement>(
          'input:not(:disabled)'
        )

        if (input && input.required && input.value.trim() === '') {
          showError(
            field,
            field.dataset.validationError || 'Пожалуйста, введите пароль'
          )
          isValid = false
        }

        break
      }
      case 'email': {
        const input = field.querySelector<HTMLInputElement>(
          'input:not(:disabled)'
        )

        if (!input) {
          break
        }

        const value = input.value.trim() || ''

        if (input.required && value === '') {
          showError(
            field,
            field.dataset.validationError || 'Пожалуйста, введите email'
          )
          isValid = false
        } else if (value !== '') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          if (!emailRegex.test(value)) {
            showError(
              field,
              field.dataset.validationError ||
                'Пожалуйста, введите корректный email'
            )
            isValid = false
          }
        }

        break
      }
      case 'phone': {
        const phoneInput = field.querySelector<PhoneInput>(
          '[data-phone-input]:not(.disabled)'
        )
        const phoneController = phoneInput?.phoneInputController
        if (phoneController && !phoneController.isValid()) {
          showError(
            field,
            field.dataset.validationError ||
              'Пожалуйста, введите корректный номер телефона'
          )
          isValid = false
        }

        break
      }
      case 'checkbox': {
        const input = field.querySelector<HTMLInputElement>(
          'input:not(:disabled)'
        )

        if (input && input.required && !input.checked) {
          showError(
            field,
            field.dataset.validationError || 'Пожалуйста, подтвердите согласие'
          )
          isValid = false
        }

        break
      }

      case 'text': {
        const input = field.querySelector<
          HTMLInputElement | HTMLTextAreaElement
        >('textarea:not(:disabled), input:not(:disabled)')

        if (input && input.required && input.value.trim() === '') {
          showError(
            field,
            field.dataset.validationError || 'Пожалуйста, заполните это поле'
          )
          isValid = false
        }

        if (
          input instanceof HTMLInputElement &&
          input.dataset.type === 'date' &&
          input.value.trim() !== '' &&
          !validateDateInput(input, field)
        ) {
          isValid = false
        }

        break
      }

      case 'date': {
        const input = field.querySelector<HTMLInputElement>(
          'input:not(:disabled)'
        )

        if (!input) {
          break
        }

        if (input.required && input.value.trim() === '') {
          showError(
            field,
            field.dataset.validationError || 'Пожалуйста, введите дату'
          )
          isValid = false
        } else if (
          input.value.trim() !== '' &&
          !validateDateInput(input, field)
        ) {
          isValid = false
        }

        break
      }

      case 'rating': {
        const checkedInput = field.querySelector<HTMLInputElement>(
          'input[type="radio"]:checked'
        )

        if (!checkedInput) {
          showError(
            field,
            field.dataset.validationError || 'Пожалуйста, выберите оценку'
          )
          isValid = false
        }

        break
      }

      case 'numbers': {
        const input = field.querySelector<HTMLInputElement>(
          'input:not(:disabled)'
        )

        if (input && input.required && input.value.trim() === '') {
          showError(
            field,
            field.dataset.validationError || 'Пожалуйста, заполните это поле'
          )
          isValid = false
          break
        }

        const onlyDigitsRegex = /^\d+$/
        if (
          input &&
          input.value.trim() !== '' &&
          !onlyDigitsRegex.test(input.value)
        ) {
          showError(
            field,
            field.dataset.validationError || 'Пожалуйста, введите только цифры'
          )
          isValid = false
          break
        }

        if (input && input.value.trim() !== '') {
          const value = Number(input.value)
          const min = input.min !== '' ? Number(input.min) : null
          const max = input.max !== '' ? Number(input.max) : null

          if ((min !== null && value < min) || (max !== null && value > max)) {
            showError(
              field,
              field.dataset.validationError ||
                'Пожалуйста, введите корректное значение'
            )
            isValid = false
          }
        }

        break
      }

      case 'future-date': {
        const input = field.querySelector<HTMLInputElement>(
          'input:not(:disabled)'
        )

        if (!input) break

        if (input.required && input.value.trim() === '') {
          showError(
            field,
            field.dataset.validationError || 'Пожалуйста, введите дату'
          )
          isValid = false
          break
        }

        if (input.value.trim() !== '') {
          if (!parseMaskedDate(input.value)) {
            showError(
              field,
              field.dataset.validationError ||
                'Пожалуйста, введите дату в формате дд.мм.гггг'
            )
            isValid = false
          } else if (!isFutureOrTodayDate(input.value)) {
            showError(
              field,
              field.dataset.validationError || 'Дата не может быть в прошлом'
            )
            isValid = false
          }
        }

        break
      }

      case 'select': {
        const select = field.querySelector<HTMLSelectElement>(
          '[data-select-input]:not(:disabled)'
        )

        if (select && select.required && select.value === '') {
          showError(
            field,
            field.dataset.validationError || 'Пожалуйста, выберите значение'
          )
          isValid = false
        }

        break
      }

      case 'certificate': {
        const input = field.querySelector<HTMLInputElement>(
          'input:not(:disabled)'
        )

        if (input && input.required && input.value.trim() === '') {
          showError(
            field,
            field.dataset.validationError ||
              'Пожалуйста, введите код сертификата'
          )
          isValid = false
        } else if (input && input.value.trim() !== '') {
          // Разрешены только буквы и цифры, без маски
          const certificateRegex = /^[A-Za-z0-9]+$/

          if (!certificateRegex.test(input.value.trim())) {
            showError(
              field,
              field.dataset.validationError ||
                'Код сертификата может содержать только буквы и цифры'
            )
            isValid = false
          }
        }

        break
      }
    }
  })

  return isValid
}

function validateDateInput(
  input: HTMLInputElement,
  field: HTMLElement
): boolean {
  const value = input.value.trim()

  if (!parseMaskedDate(value)) {
    showError(
      field,
      field.dataset.validationError ||
        'Пожалуйста, введите дату в формате дд.мм.гггг'
    )
    return false
  }

  if (!isPastDate(value)) {
    showError(
      field,
      field.dataset.validationError ||
        'Дата должна быть раньше сегодняшнего дня'
    )
    return false
  }

  return true
}

/**
 * Очищает все сообщения об ошибках в форме.
 *
 * @private
 * @param {HTMLFormElement} form - Форма для очистки ошибок
 * @return {void}
 */
export function clearErrors(form: HTMLFormElement): void {
  const errorEls = form.querySelectorAll<HTMLElement>('[data-error]')
  errorEls.forEach((el) => (el.textContent = ''))
}

/**
 * Отображает сообщение об ошибке для указанного поля.
 *
 * @private
 * @param {HTMLElement} field - Поле с ошибкой
 * @param {string} message - Текст сообщения об ошибке
 * @return {void}
 */
export function showError(field: HTMLElement, message: string) {
  const errorEl = field.querySelector(':scope > [data-error]') as HTMLElement

  if (errorEl) {
    errorEl.textContent = message
  }
}

/**
 * Очищает код сертификата от недопустимых символов
 *
 * @param {string} value - Введенное значение
 * @return {string} Код сертификата только из букв и цифр
 */
export function formatCertificateCode(value: string): string {
  return value.replace(/[^A-Za-z0-9]/g, '')
}

/**
 * Устанавливает фильтрацию ввода для поля кода сертификата
 *
 * @param {HTMLInputElement} input - Поле ввода
 * @return {void}
 */
export function setupCertificateCodeMask(input: HTMLInputElement): void {
  input.addEventListener('input', (event) => {
    const target = event.target as HTMLInputElement
    const cleaned = formatCertificateCode(target.value)
    target.value = cleaned
  })
}
